import type { AIModeState, RankedSpace } from "../types";
import { formatUTCDate } from "../../proposal-standard/utils";
import { DEFAULT_CONTRACT_TERM } from "../types";

export interface FieldChange {
  label: string;
  from: string;
  to: string;
}

export interface SpaceChanges {
  spaceId: string;
  spaceName: string;
  roomName: string;
  changes: FieldChange[];
}

export interface ProposalDiff {
  spacesAdded: RankedSpace[];
  spacesRemoved: RankedSpace[];
  recommendedAdded: RankedSpace[];
  recommendedRemoved: RankedSpace[];
  globalChanges: FieldChange[];
  spaceAdjustmentChanges: SpaceChanges[];
  diagnosisChanges: FieldChange[];
  hasChanges: boolean;
  totalCount: number;
}

function fmtPrice(v: number): string {
  return `R$ ${Math.round(v).toLocaleString("pt-BR")}`;
}

function fmtPct(v: number): string {
  return `${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function fmtDate(timestamp: number): string {
  return formatUTCDate(timestamp);
}

function fmtBool(v: boolean | undefined): string {
  return v ? "Ativo" : "Inativo";
}

function fmtMonths(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v} meses`;
}

function fieldChange(
  label: string,
  from: string | undefined | null,
  to: string | undefined | null,
): FieldChange | null {
  const f = from ?? "";
  const t = to ?? "";
  if (f === t) return null;
  return { label, from: f, to: t };
}

function parseNumeric(s: string | undefined | null): number | null {
  if (!s || s.trim() === "") return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

function numericStringChange(
  label: string,
  from: string | undefined | null,
  to: string | undefined | null,
): FieldChange | null {
  if (parseNumeric(from) === parseNumeric(to)) return null;
  return fieldChange(label, from, to);
}

function push(changes: FieldChange[], c: FieldChange | null) {
  if (c) changes.push(c);
}

function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

// Compares the curated photo lists (selection + order) of a space section.
function imagesChange(
  label: string,
  from: string[] | undefined | null,
  to: string[] | undefined | null,
): FieldChange | null {
  const a = from ?? [];
  const b = to ?? [];
  if (arraysEqual(a, b)) return null;
  const fmt = (arr: string[]) =>
    `${arr.length} foto${arr.length === 1 ? "" : "s"}`;
  // Same count but different content => only selection/order changed.
  const toLabel =
    a.length === b.length ? `${fmt(b)} (seleção/ordem alterada)` : fmt(b);
  return { label, from: fmt(a), to: toLabel };
}

export function computeProposalDiff(
  original: AIModeState,
  current: AIModeState,
  allSpaces: RankedSpace[],
): ProposalDiff {
  const spaceById = new Map(allSpaces.map((s) => [s.id, s]));

  // --- Spaces added / removed ---
  const origIds = new Set(original.location?.selectedSpaceIds ?? []);
  const currIds = new Set(current.location?.selectedSpaceIds ?? []);

  const spacesAdded: RankedSpace[] = [];
  const spacesRemoved: RankedSpace[] = [];

  for (const id of currIds) {
    if (!origIds.has(id)) {
      const s = spaceById.get(id);
      if (s) spacesAdded.push(s);
    }
  }
  for (const id of origIds) {
    if (!currIds.has(id)) {
      const s = spaceById.get(id);
      if (s) spacesRemoved.push(s);
    }
  }

  // --- Recommended spaces ---
  const origRec = new Set(original.adjustments?.recommendedSpaceIds ?? []);
  const currRec = new Set(current.adjustments?.recommendedSpaceIds ?? []);

  const recommendedAdded: RankedSpace[] = [];
  const recommendedRemoved: RankedSpace[] = [];

  for (const id of currRec) {
    if (!origRec.has(id)) {
      const s = spaceById.get(id);
      if (s) recommendedAdded.push(s);
    }
  }
  for (const id of origRec) {
    if (!currRec.has(id)) {
      const s = spaceById.get(id);
      if (s) recommendedRemoved.push(s);
    }
  }

  // --- Global adjustment changes ---
  const globalChanges: FieldChange[] = [];
  const oa = original.adjustments;
  const ca = current.adjustments;

  push(
    globalChanges,
    fieldChange(
      "Crédito",
      fmtPrice(oa?.creditsPrice ?? 0),
      fmtPrice(ca?.creditsPrice ?? 0),
    ),
  );
  push(
    globalChanges,
    fieldChange(
      "Prazo do contrato",
      fmtMonths(oa?.contractMonths ?? DEFAULT_CONTRACT_TERM),
      fmtMonths(ca?.contractMonths ?? DEFAULT_CONTRACT_TERM),
    ),
  );
  if (oa?.proposalValidity != null || ca?.proposalValidity != null) {
    const fromDate = oa?.proposalValidity ? fmtDate(oa.proposalValidity) : "—";
    const toDate = ca?.proposalValidity ? fmtDate(ca.proposalValidity) : "—";
    push(globalChanges, fieldChange("Validade", fromDate, toDate));
  }
  push(
    globalChanges,
    fieldChange(
      "Notas do vendedor",
      oa?.sellerNotes ?? "",
      ca?.sellerNotes ?? "",
    ),
  );
  push(
    globalChanges,
    fieldChange(
      "Exibir License Fee?",
      fmtBool(oa?.showLicenseFee),
      fmtBool(ca?.showLicenseFee),
    ),
  );
  if (ca?.showLicenseFee || oa?.showLicenseFee) {
    push(
      globalChanges,
      fieldChange(
        "Percentual License Fee",
        fmtPct(oa?.licenseFeePercentage ?? 8),
        fmtPct(ca?.licenseFeePercentage ?? 8),
      ),
    );
    push(
      globalChanges,
      fieldChange(
        "Valor License Fee",
        fmtPrice(oa?.licenseFeeFixed ?? 0),
        fmtPrice(ca?.licenseFeeFixed ?? 0),
      ),
    );
  }
  push(
    globalChanges,
    fieldChange(
      "Setup Fee",
      fmtBool(oa?.includeSetupFee),
      fmtBool(ca?.includeSetupFee),
    ),
  );
  if (ca?.includeSetupFee || oa?.includeSetupFee) {
    push(
      globalChanges,
      fieldChange(
        "Valor Setup Fee",
        fmtPrice(oa?.setupFee ?? 0),
        fmtPrice(ca?.setupFee ?? 0),
      ),
    );
  }
  push(
    globalChanges,
    fieldChange(
      "Créditos Flex",
      fmtBool(oa?.includeFlexCredits),
      fmtBool(ca?.includeFlexCredits),
    ),
  );
  if (ca?.includeFlexCredits || oa?.includeFlexCredits) {
    push(
      globalChanges,
      fieldChange(
        "Quantidade Créditos Flexíveis",
        String(oa?.flexCredits ?? 0),
        String(ca?.flexCredits ?? 0),
      ),
    );
  }

  // --- Per-space changes (adjustments + images) ---
  const spaceAdjustmentChanges: SpaceChanges[] = [];
  const origAdj = new Map(
    (original.adjustments?.spaceAdjustments ?? []).map((a) => [a.spaceId, a]),
  );
  const currAdj = new Map(
    (current.adjustments?.spaceAdjustments ?? []).map((a) => [a.spaceId, a]),
  );
  const origSpaces = new Map(
    (original.adjustments?.selectedSpaces ?? []).map((s) => [s.id, s]),
  );
  const currSpaces = new Map(
    (current.adjustments?.selectedSpaces ?? []).map((s) => [s.id, s]),
  );

  const perSpaceIds = new Set<string>([
    ...currAdj.keys(),
    ...currSpaces.keys(),
  ]);

  for (const spaceId of perSpaceIds) {
    const changes: FieldChange[] = [];

    const orig = origAdj.get(spaceId);
    const curr = currAdj.get(spaceId);
    if (orig && curr) {
      push(
        changes,
        fieldChange(
          "Valor extra",
          fmtPrice(orig.extraValue),
          fmtPrice(curr.extraValue),
        ),
      );
      push(
        changes,
        fieldChange("Margem", fmtPct(orig.margin), fmtPct(curr.margin)),
      );
      push(
        changes,
        fieldChange(
          "Valor mensal",
          fmtPrice(orig.totalPrice),
          fmtPrice(curr.totalPrice),
        ),
      );
      push(
        changes,
        fieldChange(
          "Setup",
          fmtPrice(orig.setupCost ?? 0),
          fmtPrice(curr.setupCost ?? 0),
        ),
      );
    }

    // Image curation (selection + order) for the Sala and Espaço sections.
    // Only compared when both sides carry an explicit curated list, so legacy
    // proposals (saved before curation existed) don't produce phantom diffs.
    const origSpace = origSpaces.get(spaceId);
    const currSpace = currSpaces.get(spaceId);
    if (origSpace && currSpace) {
      if (
        origSpace.selectedImages != null &&
        currSpace.selectedImages != null
      ) {
        push(
          changes,
          imagesChange(
            "Fotos da sala",
            origSpace.selectedImages,
            currSpace.selectedImages,
          ),
        );
      }
      if (
        origSpace.selectedSpacePhotos != null &&
        currSpace.selectedSpacePhotos != null
      ) {
        push(
          changes,
          imagesChange(
            "Fotos do espaço",
            origSpace.selectedSpacePhotos,
            currSpace.selectedSpacePhotos,
          ),
        );
      }
    }

    if (changes.length > 0) {
      const space = spaceById.get(spaceId) ?? currSpace ?? origSpace;
      spaceAdjustmentChanges.push({
        spaceId,
        spaceName: space?.spaceName ?? spaceId,
        roomName: space?.roomName ?? spaceId,
        changes,
      });
    }
  }

  // --- Diagnosis changes ---
  const diagnosisChanges: FieldChange[] = [];
  const od = original.diagnosis;
  const cd = current.diagnosis;

  push(
    diagnosisChanges,
    fieldChange("Empresa", od?.companyName ?? "", cd?.companyName ?? ""),
  );
  push(
    diagnosisChanges,
    fieldChange("Segmento", od?.segment ?? "", cd?.segment ?? ""),
  );
  push(
    diagnosisChanges,
    fieldChange("Contato", od?.contactName ?? "", cd?.contactName ?? ""),
  );
  push(
    diagnosisChanges,
    fieldChange("E-mail", od?.contactEmail ?? "", cd?.contactEmail ?? ""),
  );
  push(
    diagnosisChanges,
    fieldChange("Cidade", od?.targetCity ?? "", cd?.targetCity ?? ""),
  );
  push(
    diagnosisChanges,
    fieldChange(
      "Posições mín.",
      String(od?.positionsMin ?? ""),
      String(cd?.positionsMin ?? ""),
    ),
  );
  push(
    diagnosisChanges,
    fieldChange(
      "Posições máx.",
      String(od?.positionsMax ?? ""),
      String(cd?.positionsMax ?? ""),
    ),
  );
  push(
    diagnosisChanges,
    numericStringChange(
      "Orçamento mensal",
      od?.monthlyBudget ?? "",
      cd?.monthlyBudget ?? "",
    ),
  );
  push(
    diagnosisChanges,
    fieldChange("Necessidades", od?.needs ?? "", cd?.needs ?? ""),
  );

  const totalCount =
    spacesAdded.length +
    spacesRemoved.length +
    recommendedAdded.length +
    recommendedRemoved.length +
    globalChanges.length +
    spaceAdjustmentChanges.reduce((s, sc) => s + sc.changes.length, 0) +
    diagnosisChanges.length;

  return {
    spacesAdded,
    spacesRemoved,
    recommendedAdded,
    recommendedRemoved,
    globalChanges,
    spaceAdjustmentChanges,
    diagnosisChanges,
    hasChanges: totalCount > 0,
    totalCount,
  };
}
